import { FieldValues } from 'react-hook-form';
import { TranslateFunction } from '~/Locale';
import { TextFieldProps } from './types';
import { regexNationalIdentificationNumber } from '~/helpers/regex';
import {
  isValidCity,
  isValidPersonName,
  isValidStreetAddress,
  normalizeLatinText
} from '~/helpers/latinText';
import {
  CITY_MAX_LENGTH,
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
  STREET_ADDRESS_MAX_LENGTH
} from '~/App/shared/validation/constants';

interface FieldRulesArgs<FormState extends FieldValues> {
  t: TranslateFunction;
  field?: string;
  message?: string;
  rules?: TextFieldProps<FormState>['rules'];
}

export const fieldRuleIsRequired = <FormState extends FieldValues>({
  t,
  field,
  message
}: FieldRulesArgs<FormState>) => ({
  required: {
    value: true,
    message: message ? message : `${field} ${t(`can't be blank`)}`
  }
});

export const fieldRuleNationalIdentificationNumberPattern = (
  t: TranslateFunction
) => ({
  value: regexNationalIdentificationNumber,
  message: t('National identification number is invalid')
});

const toText = (value: unknown) => (typeof value === 'string' ? value : '');

interface LatinTextRuleArgs {
  isValid: (value: string) => boolean;
  invalidMessage: string;
  maxLength: number;
  tooLongMessage: string;
  blankMessage?: string;
}

const fieldRuleLatinText = ({
  isValid,
  invalidMessage,
  maxLength,
  tooLongMessage,
  blankMessage
}: LatinTextRuleArgs) => ({
  ...(blankMessage
    ? {
        notBlank: (value: unknown) =>
          normalizeLatinText(toText(value)) !== '' || blankMessage
      }
    : {}),
  latinText: (value: unknown) => isValid(toText(value)) || invalidMessage,
  maxLength: (value: unknown) =>
    normalizeLatinText(toText(value)).length <= maxLength || tooLongMessage
});

const personNameInvalidMessage = (t: TranslateFunction) =>
  t(
    'Please enter your real name. Numbers and special characters such as * and ● are not allowed.'
  );

export const fieldRuleFirstName = (t: TranslateFunction, required = true) =>
  fieldRuleLatinText({
    isValid: isValidPersonName,
    invalidMessage: personNameInvalidMessage(t),
    maxLength: FIRST_NAME_MAX_LENGTH,
    tooLongMessage: t(
      'First name is too long (maximum is %s characters)',
      FIRST_NAME_MAX_LENGTH
    ),
    blankMessage: required ? t(`First name can't be blank`) : undefined
  });

export const fieldRuleLastName = (t: TranslateFunction, required = true) =>
  fieldRuleLatinText({
    isValid: isValidPersonName,
    invalidMessage: personNameInvalidMessage(t),
    maxLength: LAST_NAME_MAX_LENGTH,
    tooLongMessage: t(
      'Last name is too long (maximum is %s characters)',
      LAST_NAME_MAX_LENGTH
    ),
    blankMessage: required ? t(`Last name can't be blank`) : undefined
  });

export const fieldRuleCity = (t: TranslateFunction, required = true) =>
  fieldRuleLatinText({
    isValid: isValidCity,
    invalidMessage: t(
      'Please enter a real city. Numbers and special characters such as * and ● are not allowed.'
    ),
    maxLength: CITY_MAX_LENGTH,
    tooLongMessage: t(
      'City is too long (maximum is %s characters)',
      CITY_MAX_LENGTH
    ),
    blankMessage: required ? t(`City can't be blank`) : undefined
  });

export const fieldRuleStreetAddress = (t: TranslateFunction, required = true) =>
  fieldRuleLatinText({
    isValid: isValidStreetAddress,
    invalidMessage: t(
      'Please enter a real address. Special characters such as * and ● are not allowed.'
    ),
    maxLength: STREET_ADDRESS_MAX_LENGTH,
    tooLongMessage: t(
      'Address is too long (maximum is %s characters)',
      STREET_ADDRESS_MAX_LENGTH
    ),
    blankMessage: required ? t(`Address can't be blank`) : undefined
  });
